/**
 * Builds a Vercel AI SDK language model from user settings and the shared
 * call defaults (spec section 11, "Implementation rules").
 *
 * The key stays in the browser (IndexedDB) and is sent only to the endpoint
 * the user configured: Google's Gemini API, OpenAI, or the user's own
 * OpenAI-compatible base URL. Never log settings.
 */
import { createGoogleGenerativeAI } from '@ai-sdk/google';
import { createOpenAI } from '@ai-sdk/openai';
import { createOpenAICompatible } from '@ai-sdk/openai-compatible';
import { extractJsonMiddleware, wrapLanguageModel, type JSONValue, type LanguageModel } from 'ai';
import type { AiSettings } from '@/domain/types';
import { bareModelId, generationOf, minimumThinkingLevelFor } from './models';

export { DEFAULT_AI_SETTINGS, DEFAULT_GEMINI_MODEL } from '@/domain/settings/defaults';

export const GEMINI_API_BASE = 'https://generativelanguage.googleapis.com/v1beta';
export const OPENAI_API_BASE = 'https://api.openai.com/v1';

/** Call limits from the spec: deterministic, bounded, and never hanging. */
export const AI_CALL_LIMITS = Object.freeze({
  temperature: 0,
  maxOutputTokens: 4096,
  maxRetries: 2,
  timeoutMs: 90_000,
});

/** A model instance `wrapLanguageModel` accepts (used for test injection). */
export type WrappableModel = Parameters<typeof wrapLanguageModel>[0]['model'];

export function isAiConfigured(settings: AiSettings): boolean {
  if (!settings.model) return false;
  switch (settings.provider) {
    case 'gemini':
    case 'openai':
      return settings.apiKey.length > 0;
    case 'openai-compatible':
      return settings.baseUrl.length > 0;
    default: {
      const exhaustive: never = settings.provider;
      throw new Error(`Unknown AI provider ${String(exhaustive)}`);
    }
  }
}

export function normalizeBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

function rawModel(settings: AiSettings): WrappableModel {
  switch (settings.provider) {
    case 'gemini':
      // The SDK prepends "models/" itself; a stored id that already has it would 404.
      return createGoogleGenerativeAI({ apiKey: settings.apiKey })(bareModelId(settings.model));
    case 'openai':
      return createOpenAI({ apiKey: settings.apiKey })(settings.model);
    case 'openai-compatible':
      return createOpenAICompatible({
        name: 'custom',
        baseURL: normalizeBaseUrl(settings.baseUrl),
        // Local servers often accept any key; send a placeholder so the header exists.
        apiKey: settings.apiKey || 'not-needed',
      })(settings.model);
    default: {
      const exhaustive: never = settings.provider;
      throw new Error(`Unknown AI provider ${String(exhaustive)}`);
    }
  }
}

/**
 * The configured model wrapped with `extractJsonMiddleware`, which strips the
 * markdown fences Flash-Lite (and most local models) put around JSON.
 */
export function createModel(settings: AiSettings): LanguageModel {
  return wrapLanguageModel({ model: rawModel(settings), middleware: extractJsonMiddleware() });
}

/** `createModel`, or an injected model (tests) wrapped the same way. */
export function resolveModel(settings: AiSettings, override?: WrappableModel): LanguageModel {
  if (override) return wrapLanguageModel({ model: override, middleware: extractJsonMiddleware() });
  return createModel(settings);
}

export type ProviderOptions = Record<string, Record<string, JSONValue>>;

/**
 * Provider-specific options per model generation.
 * - Gemini 2.5: `thinkingConfig.thinkingBudget: 0` (disables thinking).
 * - Gemini 3.x: `thinkingConfig.thinkingLevel` at the model's minimum
 *   (`'minimal'` for Flash-Lite and pre-3.7 Flash, `'low'` for 3.7+ Flash,
 *   which rejects `'minimal'` with a 400) and medium media resolution
 *   (560 tokens per image). `thinkingBudget` is rejected by 3.x.
 * - Non-Gemini ids typed under "Annan…" (Gemma etc.): nothing, because they
 *   reject `thinkingConfig`.
 * - OpenAI / compatible: nothing provider-specific; `reasoning: 'none'` is
 *   passed as a provider-agnostic call setting instead (see `reasoningFor`).
 */
export function providerOptionsFor(settings: AiSettings): ProviderOptions {
  switch (settings.provider) {
    case 'gemini': {
      const generation = generationOf(settings.model);
      switch (generation) {
        case '2.5':
          return { google: { thinkingConfig: { thinkingBudget: 0 } } };
        case '3.x':
          return {
            google: {
              thinkingConfig: { thinkingLevel: minimumThinkingLevelFor(settings.model) },
              mediaResolution: 'MEDIA_RESOLUTION_MEDIUM',
            },
          };
        case 'other':
          return {};
        default: {
          const exhaustive: never = generation;
          throw new Error(`Unknown Gemini generation ${String(exhaustive)}`);
        }
      }
    }
    case 'openai':
    case 'openai-compatible':
      return {};
    default: {
      const exhaustive: never = settings.provider;
      throw new Error(`Unknown AI provider ${String(exhaustive)}`);
    }
  }
}

/**
 * The provider-agnostic `reasoning` call setting, or `undefined` when it must
 * be left out:
 * - Gemini 2.5 / 3.x and OpenAI: `'none'` (the OpenAI provider downgrades an
 *   unsupported effort to the model's minimum with a warning, never a 400).
 * - Non-Gemini ids on the Gemini endpoint: omitted; the SDK would translate
 *   `'none'` into a `thinkingBudget` that Gemma rejects.
 * - OpenAI-compatible: omitted; the SDK forwards it verbatim as
 *   `reasoning_effort: 'none'`, which strict local servers reject.
 */
export function reasoningFor(settings: AiSettings): 'none' | undefined {
  switch (settings.provider) {
    case 'gemini':
      return generationOf(settings.model) === 'other' ? undefined : 'none';
    case 'openai':
      return 'none';
    case 'openai-compatible':
      return undefined;
    default: {
      const exhaustive: never = settings.provider;
      throw new Error(`Unknown AI provider ${String(exhaustive)}`);
    }
  }
}

/**
 * The call settings every structured call shares. Spread into `generateText`.
 * `maxOutputTokens` can be raised per call (statement extraction).
 */
export function callDefaults(settings: AiSettings, opts: { maxOutputTokens?: number; signal?: AbortSignal | undefined } = {}) {
  const reasoning = reasoningFor(settings);
  return {
    temperature: AI_CALL_LIMITS.temperature,
    maxOutputTokens: opts.maxOutputTokens ?? AI_CALL_LIMITS.maxOutputTokens,
    maxRetries: AI_CALL_LIMITS.maxRetries,
    timeout: AI_CALL_LIMITS.timeoutMs,
    ...(reasoning ? { reasoning } : {}),
    providerOptions: providerOptionsFor(settings),
    ...(opts.signal ? { abortSignal: opts.signal } : {}),
  };
}

/** Pacing derived from the plan: free tier is sequential with backoff, paid may run in parallel. */
export function concurrencyFor(settings: AiSettings): 1 | 2 | 3 {
  if (settings.provider === 'gemini' && settings.geminiPlan === 'free') return 1;
  return settings.parallel;
}
